import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { schoolService } from '@/api/services/schoolService'
import { restClient, getApiError } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { toast } from '@/store/useToastStore'
import { cn, DUMMY_AVATAR, onAvatarError } from '@/shared/utils'
import { Spinner } from '@/shared/ui/Spinner'
import {
  ChevronLeft, Plus, Eye, Mail, Phone, Calendar, MapPin,
  Star, Users, BookOpen, DollarSign, Award, Percent,
  Globe, CheckCircle, TrendingUp, Layers, Edit2, X, Search,
} from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────
function getSchoolName(s: any) {
  const tr = s?.vendor_translations?.find((t: any) => t.locale?.toLowerCase() === 'en')
    ?? s?.vendor_translations?.[0]
  return tr?.name ?? s?.name ?? 'Unknown School'
}
function fmtDate(v?: string | null) {
  if (!v) return '—'
  try { return new Date(v).toLocaleDateString('en-GB').replace(/\//g, '-') } catch { return v }
}
function trainerStatusStyle(s: string) {
  if (s === 'Active')    return 'bg-green-500 text-white'
  if (s === 'Suspended') return 'bg-red-600 text-white'
  if (s === 'On Leave')  return 'bg-brand-500 text-white'
  return 'bg-red-100 text-red-600'
}
function bookingStatusStyle(s: string) {
  const u = s?.toUpperCase()
  if (u === 'COMPLETED')                          return 'bg-green-100 text-green-700'
  if (u === 'CONFIRMED')                          return 'bg-brand-100 text-brand-700'
  if (u === 'CANCELLED')                          return 'bg-red-100 text-red-600'
  if (u === 'IN_PROGRESS' || u === 'IN PROGRESS') return 'bg-brand-100 text-brand-700'
  return 'bg-gray-100 text-gray-600'
}

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TABS = [
  { value: 'details',         label: 'School Details'  },
  { value: 'package',         label: 'Package'         },
  { value: 'trainer',         label: 'Trainer'         },
  { value: 'account-summary', label: 'Account Summary' },
]

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse bg-gray-200 rounded-lg', className)} />
}

// ── Pagination helper ─────────────────────────────────────────────────────────
function Paginator({ page, lastPage, total, perPage, onPage }: {
  page: number; lastPage: number; total: number; perPage: number; onPage: (p: number) => void
}) {
  const pageNums = (() => {
    let s = Math.max(1, page - 2)
    if (s + 4 > lastPage) s = Math.max(1, lastPage - 4)
    return Array.from({ length: Math.min(5, lastPage) }, (_, i) => s + i).filter(p => p <= lastPage)
  })()
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
      <p className="text-xs text-gray-500">{(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total} items</p>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1}
          className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">
          <ChevronLeft size={13} />
        </button>
        {pageNums.map(p => (
          <button key={p} onClick={() => onPage(p)}
            className={cn('w-7 h-7 rounded-lg border text-xs font-semibold',
              p === page ? 'bg-brand-500 text-white border-brand-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
            {p}
          </button>
        ))}
        <button onClick={() => onPage(Math.min(lastPage, page + 1))} disabled={page >= lastPage}
          className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">
          <ChevronLeft size={13} className="rotate-180" />
        </button>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — School Details
// ══════════════════════════════════════════════════════════════════════════════
function DetailsTab({ school }: { school: any }) {
  const name        = getSchoolName(school)
  const email       = school.email ?? '—'
  const phone       = school.phone_number ?? school.phone ?? '—'
  const website     = school.website ?? '—'
  const established = fmtDate(school.established_date ?? school.created_at)
  const logo        = school.logo ?? null
  // `is_active` is the authoritative flag — the API's `status` string can disagree with it.
  const isActive    = !!school.is_active
  const about       = school.vendor_translations?.find((t: any) => t.locale?.toLowerCase() === 'en')?.about
    ?? school.about ?? ''

  const minCommission = school.min_commision    ?? school.min_commission    ?? 0
  const maxCommission = school.max_commision    ?? school.max_commission    ?? 0
  const minCertComm   = school.certificate_min_commision ?? school.cert_min_commission ?? 0
  const maxCertComm   = school.certificate_max_commision ?? school.cert_max_commission ?? 0

  const totalRevenue        = school.total_revenue    ?? 0
  const schoolProfit        = school.school_profit    ?? school.profit ?? 0
  const totalTrainer        = school.total_trainers    ?? school.trainers_count  ?? 0
  const activeTrainers      = school.active_trainers  ?? 0
  const unavailableTrainers = school.unavailable_trainers ?? 0
  const totalStudents       = school.total_students    ?? school.students_count  ?? 0
  const activeStudents      = school.active_students  ?? 0
  const completedStudents   = school.completed_students ?? 0

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

      {/* ── Left column ── */}
      <div className="space-y-5">

        {/* School Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-brand-500 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white" />
            <span className="text-sm font-bold text-white">School Basic Info</span>
          </div>
          <div className="p-5 flex gap-4">
            <div className="flex-shrink-0">
              {logo ? (
                <img src={logo} alt={name} className="w-20 h-20 rounded-xl object-cover border border-gray-200 shadow-sm" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-brand-100 flex items-center justify-center text-brand-700 text-2xl font-black border border-gray-200 shadow-sm">
                  {name.slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-gray-900">{name}</h2>
                <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-bold',
                  isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white')}>
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="text-xs text-gray-400">School ID: #{school.id?.toString().padStart(4, '0') ?? '0000'}</div>
              <div className="space-y-1">
                {[
                  { icon: <Users size={11} />, label: 'Name (Arabic)', value: school.vendor_translations?.find((t: any) => t.locale === 'ar')?.name ?? '—' },
                  { icon: <Mail size={11} />, label: 'Email', value: email },
                  { icon: <Phone size={11} />, label: 'Phone Number', value: phone },
                  { icon: <Globe size={11} />, label: 'Website', value: website, link: true },
                  { icon: <Calendar size={11} />, label: 'Established Date', value: established },
                ].map(({ icon, label, value, link }) => (
                  <div key={label} className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="text-gray-400 flex-shrink-0">{icon}</span>
                    <span className="text-gray-400 w-24 flex-shrink-0">{label}:</span>
                    {link && value !== '—' ? (
                      <a href={value} target="_blank" rel="noreferrer" className="text-brand-500 hover:underline truncate">{value}</a>
                    ) : (
                      <span className="font-medium truncate">{value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* About */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-brand-500 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white" />
            <span className="text-sm font-bold text-white">About</span>
          </div>
          <div className="p-5 text-sm text-gray-600 leading-relaxed min-h-[80px]">
            {about || <span className="text-gray-400 italic">No description available.</span>}
          </div>
        </div>

        {/* Overview */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-brand-500 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white" />
            <span className="text-sm font-bold text-white">Overview</span>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <div className="text-xs font-bold text-gray-700 mb-2">Trainer ({totalTrainer})</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-green-50 border border-green-100 p-3 text-center">
                  <div className="text-2xl font-black text-green-600">{activeTrainers}</div>
                  <div className="text-xs text-green-500 font-semibold mt-0.5">Active Trainer</div>
                </div>
                <div className="rounded-xl bg-red-50 border border-red-100 p-3 text-center">
                  <div className="text-2xl font-black text-red-400">{unavailableTrainers}</div>
                  <div className="text-xs text-red-400 font-semibold mt-0.5">Unavailable Trainer</div>
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-gray-700 mb-2">Student ({totalStudents})</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-brand-50 border border-brand-100 p-3 text-center">
                  <div className="text-2xl font-black text-brand-600">{activeStudents}</div>
                  <div className="text-xs text-brand-500 font-semibold mt-0.5">Active Student</div>
                </div>
                <div className="rounded-xl bg-purple-50 border border-purple-100 p-3 text-center">
                  <div className="text-2xl font-black text-purple-400">{completedStudents}</div>
                  <div className="text-xs text-purple-400 font-semibold mt-0.5">Completed Student</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right column ── */}
      <div className="space-y-5">

        {/* Revenue */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-brand-500 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white" />
            <span className="text-sm font-bold text-white">Revenue</span>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="rounded-xl bg-brand-50 border border-brand-100 p-4">
                <div className="text-xl font-black text-brand-600">AED {Number(totalRevenue).toLocaleString()}</div>
                <div className="text-xs text-gray-500 mt-1">Total Revenue</div>
              </div>
              <div className="rounded-xl bg-green-50 border border-green-100 p-4">
                <div className="text-xl font-black text-green-600">AED {Number(schoolProfit).toLocaleString()}</div>
                <div className="text-xs text-gray-500 mt-1">School Profit</div>
              </div>
            </div>
            <button className="w-full py-2 rounded-xl border border-brand-300 text-brand-600 text-xs font-semibold hover:bg-brand-50 transition-colors">
              View All Revenue
            </button>
          </div>
        </div>

        {/* Commission Settings */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-teal-500 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white" />
            <span className="text-sm font-bold text-white">Commission Settings</span>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <div className="text-xs font-bold text-gray-700 mb-2">School Commission (%)</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-center">
                  <div className="text-lg font-black text-gray-800">{minCommission}%</div>
                  <div className="text-[12px] text-gray-400 font-semibold">Min Commission</div>
                </div>
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-center">
                  <div className="text-lg font-black text-gray-800">{maxCommission}%</div>
                  <div className="text-[12px] text-gray-400 font-semibold">Max Commission</div>
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-gray-700 mb-2">Certificate Commission (%)</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-center">
                  <div className="text-lg font-black text-gray-800">{minCertComm}%</div>
                  <div className="text-[12px] text-gray-400 font-semibold">Min Commission</div>
                </div>
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-center">
                  <div className="text-lg font-black text-gray-800">{maxCertComm}%</div>
                  <div className="text-[12px] text-gray-400 font-semibold">Max Commission</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Packages preview */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-teal-500 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white" />
            <span className="text-sm font-bold text-white">Packages</span>
          </div>
          <div className="p-5 space-y-2">
            {(school.packages ?? []).length === 0 ? (
              <p className="text-xs text-gray-400 italic">No packages linked yet.</p>
            ) : (
              (school.packages ?? []).slice(0, 4).map((pkg: any) => {
                const pkgName = pkg.package_translations?.find((t: any) => t.locale?.toLowerCase() === 'en')?.name ?? pkg.name ?? 'Package'
                const price   = pkg.price ?? 0
                return (
                  <div key={pkg.id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-brand-50 border border-brand-100">
                    <span className="text-xs font-semibold text-gray-800">{pkgName}</span>
                    <span className="text-xs font-bold text-brand-600">{price} AED</span>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — Package
// API: GET /admin/package/get-packge-list?locale=en&vendor_id=X
// Key fields: background_color, min_price, max_price, number_of_sessions,
//   is_unlimited, is_certificate_included, free_km, free_pickup_count,
//   free_reschedule_count, free_trainer_change_count,
//   package_translations[].{ locale, name, session_inclusions }
// ══════════════════════════════════════════════════════════════════════════════
function PackageTab({ schoolId }: { schoolId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['school-packages', schoolId],
    queryFn: () => restClient.get(
      `${endpoints.package.list}?locale=en&vendor_id=${schoolId}&page=1&limit=50`
    ).then(r => r.data),
    enabled: !!schoolId,
  })

  const packages: any[] = data?.data ?? []

  const COLOR_MAP: Record<string, { bg: string; border: string; title: string; price: string; check: string }> = {
    bronze:    { bg: 'bg-brand-50',  border: 'border-brand-200',  title: 'text-brand-800',  price: 'text-brand-600',  check: 'text-brand-500'  },
    silver:    { bg: 'bg-gray-50',   border: 'border-gray-300',   title: 'text-gray-700',   price: 'text-gray-600',   check: 'text-gray-500'   },
    gold:      { bg: 'bg-yellow-50', border: 'border-yellow-300', title: 'text-yellow-800', price: 'text-yellow-600', check: 'text-yellow-500' },
    unlimited: { bg: 'bg-teal-50',   border: 'border-teal-200',   title: 'text-teal-700',   price: 'text-teal-600',   check: 'text-teal-500'   },
    express:   { bg: 'bg-red-50',    border: 'border-red-200',    title: 'text-red-800',    price: 'text-red-600',    check: 'text-red-500'    },
    red:       { bg: 'bg-red-50',    border: 'border-red-200',    title: 'text-red-800',    price: 'text-red-600',    check: 'text-red-500'    },
    default:   { bg: 'bg-brand-50',   border: 'border-brand-200',   title: 'text-brand-700',   price: 'text-brand-600',   check: 'text-brand-500'   },
  }

  if (isLoading) return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
      {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-72" />)}
    </div>
  )

  if (packages.length === 0) return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Layers size={40} className="text-gray-200 mb-3" />
      <p className="text-sm font-semibold text-gray-500">No packages available</p>
    </div>
  )

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
      {packages.map((pkg: any) => {
        const colorKey = pkg.background_color?.toLowerCase() ?? 'default'
        const col      = COLOR_MAP[colorKey] ?? COLOR_MAP.default

        const trans = pkg.package_translations?.find(
          (t: any) => t.locale?.toLowerCase() === 'en'
        ) ?? pkg.package_translations?.[0]

        const name       = trans?.name ?? 'Package'
        const minPrice   = Math.round(Number(pkg.min_price ?? 0))
        const maxPrice   = Math.round(Number(pkg.max_price ?? 0))
        const priceDisplay = minPrice === maxPrice ? `${minPrice} AED` : `${minPrice}–${maxPrice} AED`
        const sessions   = pkg.is_unlimited ? 'Unlimited' : `${pkg.number_of_sessions ?? 0} Sessions`
        const hasCert    = pkg.is_certificate_included

        const features = [
          sessions,
          hasCert && 'Certificate included',
          pkg.free_km                   ? `${pkg.free_km} km free`                                   : null,
          pkg.free_pickup_count    > 0  ? `${pkg.free_pickup_count} free pickup`                     : null,
          pkg.free_reschedule_count > 0 ? `${pkg.free_reschedule_count} free reschedule`             : null,
          pkg.free_trainer_change_count > 0 ? `${pkg.free_trainer_change_count} free trainer change` : null,
        ].filter(Boolean) as string[]

        const rawInclusions = trans?.session_inclusions ?? ''
        const htmlItems = rawInclusions
          ? rawInclusions.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').split(/\n+/).map((s: string) => s.trim()).filter(Boolean)
          : []

        return (
          <div key={pkg.id} className={cn('rounded-2xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col', col.bg, col.border)}>
            <div className="p-5 border-b border-inherit">
              <div className={cn('text-xs font-bold uppercase tracking-wider mb-1', col.title)}>
                {colorKey !== 'default' ? colorKey : ''} Package
              </div>
              <div className={cn('text-base font-bold', col.title)}>{name}</div>
              <div className={cn('text-2xl font-black mt-1', col.price)}>{priceDisplay}</div>
            </div>
            <div className="p-5 space-y-2 flex-1">
              {features.map((item, j) => (
                <div key={j} className="flex items-start gap-2 text-xs text-gray-700">
                  <CheckCircle size={12} className={cn('flex-shrink-0 mt-0.5', col.check)} />
                  <span className="capitalize">{item}</span>
                </div>
              ))}
              {htmlItems.slice(0, 4).map((item: string, j: number) => (
                <div key={`h-${j}`} className="flex items-start gap-2 text-xs text-gray-600">
                  <CheckCircle size={12} className={cn('flex-shrink-0 mt-0.5 opacity-60', col.check)} />
                  <span>{item}</span>
                </div>
              ))}
              {features.length === 0 && htmlItems.length === 0 && (
                <p className="text-xs text-gray-400 italic">No details available.</p>
              )}
            </div>
            <div className="px-5 pb-4 pt-2 border-t border-inherit flex items-center justify-between text-[12px] text-gray-400">
              <span>Sessions: <strong className="text-gray-600">{sessions}</strong></span>
              {hasCert && <span className="text-green-500 font-semibold flex items-center gap-0.5"><CheckCircle size={9} />Cert</span>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 3 — Trainer
// API: GET /admin/trainers/get-trainer-list?vendor_id=X&page=N&limit=N
// Key fields: id, gear, vehicle_number, rating, school_name, number_of_bookings,
//   user: { id, name, email, phone, country_code, photo_url, created_at },
//   vendor: { status }
// ══════════════════════════════════════════════════════════════════════════════
function TrainerTab({ schoolId, school }: { schoolId: number; school: any }) {
  const navigate            = useNavigate()
  const [page, setPage]     = useState(1)
  const perPage             = 20
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['school-trainers', schoolId, page, perPage, search],
    queryFn: () => restClient.get(
      `${endpoints.school.trainers}?vendor_id=${schoolId}&page=${page}&limit=${perPage}${search ? `&search=${search}` : ''}`
    ).then(r => r.data),
    enabled: !!schoolId,
  })

  const trainers: any[] = data?.data ?? []
  const total    = data?.meta?.total ?? data?.total ?? 0
  const lastPage = Math.ceil(total / perPage) || 1

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-2">
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
          placeholder="Search trainer…"
          className="text-xs border border-gray-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 min-w-52"
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-56" />)}
        </div>
      ) : trainers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Users size={40} className="text-gray-200 mb-3" />
          <p className="text-sm font-semibold text-gray-500">No trainers assigned</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {trainers.map((t: any) => {
            // User info is nested under t.user
            const u        = t.user ?? {}
            const name     = u.name     ?? '—'
            const email    = u.email    ?? '—'
            const phone    = u.phone    ? `+${u.country_code ?? ''} ${u.phone}` : '—'
            const photoUrl = u.photo_url ?? null
            const joinedAt = u.created_at ?? null

            // Status — active flag lives on the nested user; suspension/leave on either object
            const vendorStatus = t.vendor?.status?.toLowerCase() ?? ''
            const isActive     = vendorStatus === 'active'    || t.is_active === true || u.is_active === true
            const isSuspended  = vendorStatus === 'suspended' || t.is_suspended === true || u.is_suspended === true
            const isOnLeave    = t.is_on_leave === true
            const status       = isOnLeave ? 'On Leave' : isSuspended ? 'Suspended' : isActive ? 'Active' : 'Inactive'
            const statusCls    = isOnLeave ? 'bg-brand-500 text-white' : trainerStatusStyle(status)
            const cardBg       = status === 'Active' ? '#f0fdf4' : status === 'On Leave' ? '#fffbeb' : '#fff5f5'

            // Flat fields on trainer object (API: trainer_rating / total_bookings / total_students)
            const gear         = t.gear ?? u.user_preference?.gear ?? '—'
            const plate        = t.vehicle_number ?? '—'
            const rating       = t.trainer_rating ?? t.rating
            const ratingDisplay = (rating != null && Number(rating) !== 0) ? Number(rating).toFixed(1) : '—'
            const totalBookings = t.total_bookings ?? t.number_of_bookings ?? 0
            const activeStudents = t.total_students ?? t.total_active_students ?? 0
            const schoolName   = t.school_name ?? school?.name ?? '—'

            return (
              <div key={t.id} className="rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden bg-white">

                {/* Header */}
                <div className="p-4 flex items-start justify-between gap-3" style={{ background: cardBg }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <img src={photoUrl || DUMMY_AVATAR} alt={name} onError={onAvatarError}
                      className="w-11 h-11 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0 bg-gray-100" />
                    <div className="min-w-0">
                      <div className="font-bold text-gray-900 text-sm leading-tight truncate">{name}</div>
                      <div className="text-xs text-gray-400">ID: #TR{t.id?.toString().padStart(4, '0')} · User #{u.id}</div>
                    </div>
                  </div>
                  <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-bold flex-shrink-0', statusCls)}>{status}</span>
                </div>

                {/* Contact */}
                <div className="px-4 py-2.5 space-y-1">
                  <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1 min-w-0">
                      <Mail size={10} className="text-gray-400 flex-shrink-0" /><span className="truncate">{email}</span>
                    </span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <Phone size={10} className="text-gray-400" />{phone}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar size={10} className="text-gray-400" />Joined: {fmtDate(joinedAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin size={10} className="text-gray-400" />{schoolName}
                    </span>
                  </div>
                </div>

                {/* Key Info */}
                <div className="mx-4 mb-3 rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50 border-b border-gray-100">Key Info</div>
                  <div className="p-3 grid grid-cols-2 gap-x-4 gap-y-2">
                    {[
                      { label: 'Gear',   value: gear  },
                      { label: 'Plate',  value: plate },
                      { label: 'Rating', value: ratingDisplay, icon: <Star size={10} className="text-brand-400" /> },
                    ].map(({ label, value, icon }) => (
                      <div key={label}>
                        <div className="text-[11px] text-gray-400 font-semibold uppercase">{label}</div>
                        <div className="text-xs font-semibold text-gray-800 flex items-center gap-1">{icon}{value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats — Total Bookings + Active Students only */}
                <div className="px-4 pb-4 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-brand-50 border border-brand-100 p-2.5 text-center">
                    <div className="text-lg font-black text-brand-600">{totalBookings}</div>
                    <div className="text-[12px] text-brand-400 font-semibold">Total Bookings</div>
                  </div>
                  <div className="rounded-xl bg-green-50 border border-green-100 p-2.5 text-center">
                    <div className="text-lg font-black text-green-600">{activeStudents}</div>
                    <div className="text-[12px] text-green-500 font-semibold">Active Students</div>
                  </div>
                </div>

                {/* View Details */}
                <div className="px-4 pb-4">
                  <button
                    onClick={() => { const uid = u.id ?? t.user_id; if (uid) navigate(`/dashboard/user/${uid}`) }}
                    className="w-full py-2 rounded-xl border border-brand-300 text-brand-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-brand-50 transition-colors">
                    <Eye size={12} /> View Details
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!isLoading && total > 0 && (
        <Paginator page={page} lastPage={lastPage} total={total} perPage={perPage} onPage={setPage} />
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 4 — Student
// ══════════════════════════════════════════════════════════════════════════════
function StudentTab({ schoolId }: { schoolId: number }) {
  const [page, setPage] = useState(1)
  const perPage = 20

  const { data, isLoading } = useQuery({
    queryKey: ['school-students', schoolId, page],
    queryFn: () => restClient.get(
      `${endpoints.booking.schoolAdmin.getList}?vendor_id=${schoolId}&page=${page}&limit=${perPage}`
    ).then(r => r.data),
    enabled: !!schoolId,
  })

  const bookings: any[] = data?.data ?? []
  const total    = data?.meta?.total ?? data?.total ?? 0
  const lastPage = Math.ceil(total / perPage) || 1

  if (isLoading) return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-72" />)}
    </div>
  )

  if (bookings.length === 0) return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <BookOpen size={40} className="text-gray-200 mb-3" />
      <p className="text-sm font-semibold text-gray-500">No student bookings found</p>
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {bookings.map((b: any) => {
          const student = b.user ?? b.student ?? {}
          const trainer = b.trainer ?? {}
          const pkg     = b.package ?? {}
          const status  = b.status ?? 'Pending'
          const pkgName = pkg.package_translations?.find((t: any) => t.locale?.toLowerCase() === 'en')?.name ?? pkg.name ?? 'Package'
          const pkgPrice = b.total_amount ?? pkg.price ?? 0

          return (
            <div key={b.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">

              {/* Header */}
              <div className="p-4 flex items-start justify-between gap-3 bg-green-50/50">
                <div className="flex items-center gap-3 min-w-0">
                  <img src={student.photo_url || DUMMY_AVATAR} alt={student.name} onError={onAvatarError}
                    className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0 bg-gray-100" />
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-gray-900 truncate">{student.name ?? '—'}</div>
                    <div className="text-xs text-gray-400">ID: #ST{b.id?.toString().padStart(3, '0')}</div>
                  </div>
                </div>
                <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-bold flex-shrink-0', bookingStatusStyle(status))}>
                  {status?.replace(/_/g, ' ')}
                </span>
              </div>

              {/* Contact */}
              <div className="px-4 py-2 space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Mail size={10} className="text-gray-400" /><span className="truncate">{student.email ?? '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Phone size={10} className="text-gray-400" />{student.phone ? `+${student.country_code ?? ''} ${student.phone}` : '—'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={10} className="text-gray-400" />{fmtDate(b.created_at)}
                  </span>
                </div>
                {trainer.name && (
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Users size={10} className="text-gray-400" />Trainer: <span className="font-semibold text-gray-700">{trainer.name}</span>
                  </div>
                )}
              </div>


              {/* Payment */}
              <div className="mx-4 mb-3 rounded-xl bg-brand-50 border border-brand-100 px-3 py-2 flex items-center justify-between">
                <div>
                  <div className="text-[11px] text-brand-500 font-semibold uppercase">Payment Info</div>
                  <div className="text-xs font-bold text-gray-800">{pkgName}</div>
                </div>
                <div className="text-sm font-black text-brand-600">AED {pkgPrice}</div>
              </div>

              {/* CTA */}
              <div className="px-4 pb-4">
                <button className="w-full py-2 rounded-xl border border-brand-300 text-brand-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-brand-50 transition-colors">
                  <Eye size={12} /> View Details
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {total > 0 && (
        <Paginator page={page} lastPage={lastPage} total={total} perPage={perPage} onPage={setPage} />
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 5 — Account Summary
// ══════════════════════════════════════════════════════════════════════════════
function AccountSummaryTab({ school }: { school: any }) {
  const stats = [
    { label: 'Total Revenue',      value: `AED ${Number(school.total_revenue ?? 0).toLocaleString()}`,          icon: <DollarSign size={16} className="text-green-500" />,  bg: 'bg-green-50',   border: 'border-green-100'  },
    { label: 'School Profit',      value: `AED ${Number(school.school_profit ?? 0).toLocaleString()}`,          icon: <TrendingUp size={16} className="text-brand-500" />,   bg: 'bg-brand-50',    border: 'border-brand-100'   },
    { label: 'Total Trainers',     value: school.total_trainers   ?? 0,                                          icon: <Users size={16} className="text-purple-500" />,      bg: 'bg-purple-50',  border: 'border-purple-100' },
    { label: 'Active Trainers',    value: school.active_trainers ?? 0,                                          icon: <Users size={16} className="text-teal-500" />,        bg: 'bg-teal-50',    border: 'border-teal-100'   },
    { label: 'Total Students',     value: school.total_students   ?? 0,                                          icon: <Users size={16} className="text-brand-500" />,       bg: 'bg-brand-50',   border: 'border-brand-100'  },
    { label: 'Total Bookings',     value: school.total_bookings  ?? 0,                                          icon: <BookOpen size={16} className="text-orange-500" />,   bg: 'bg-orange-50',  border: 'border-orange-100' },
    { label: 'Total Certificates', value: school.total_certificates ?? 0,                                        icon: <Award size={16} className="text-pink-500" />,        bg: 'bg-pink-50',    border: 'border-pink-100'   },
    { label: 'Commission (%)',     value: `${school.certificate_commission_in_percentage ?? 0}%`,                icon: <Percent size={16} className="text-indigo-500" />,    bg: 'bg-indigo-50',  border: 'border-indigo-100' },
  ]

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon, bg, border }) => (
          <div key={label} className={cn('rounded-2xl border shadow-sm p-5', bg, border)}>
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm mb-2">{icon}</div>
            <div className="text-xl font-black text-gray-900">{value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <h3 className="text-sm font-bold text-gray-800 mb-4">School Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: 'School Name',    value: getSchoolName(school)                               },
            { label: 'Email',          value: school.email ?? '—'                                 },
            { label: 'Phone',          value: school.phone_number ?? school.phone ?? '—'          },
            { label: 'Owner',          value: school.vendor_user?.user?.name  ?? '—'              },
            { label: 'Owner Email',    value: school.vendor_user?.user?.email ?? '—'              },
            { label: 'Status',         value: school.is_active ? 'Active' : 'Inactive'            },
            { label: 'Registered',     value: fmtDate(school.created_at)                         },
            { label: 'Min Commission', value: `${school.min_commision ?? 0}%`                     },
            { label: 'Max Commission', value: `${school.max_commision ?? 0}%`                     },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center gap-2 text-xs">
              <span className="text-gray-400 w-32 flex-shrink-0">{label}:</span>
              <span className="font-semibold text-gray-800 truncate">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
export function SchoolDetailsPage() {
  const { id }   = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [tab, setTab] = useState('details')
  const [assignTrainerOpen, setAssignTrainerOpen] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['school-detail', id],
    queryFn:  () => schoolService.getById(id!).then((r: any) => r.data ?? r),
    enabled:  !!id,
  })

  const school     = data ?? {}
  const schoolId   = Number(id)
  // `is_active` is the authoritative flag — the API's `status` string can disagree with it.
  const isActive   = !!school.is_active
  const schoolName = getSchoolName(school)

  if (isLoading) return (
    <div className="space-y-5 animate-pulse">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-12 w-full" />
      <div className="grid grid-cols-2 gap-5">
        <Skeleton className="h-64" /><Skeleton className="h-64" />
      </div>
    </div>
  )

  if (isError) return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <p className="text-sm font-semibold text-red-500">Failed to load school details.</p>
      <button onClick={() => navigate(-1)} className="mt-4 text-xs text-brand-600 hover:underline flex items-center gap-1">
        <ChevronLeft size={12} />Go back
      </button>
    </div>
  )

  return (
    <div className="space-y-5">

      {/* ── Breadcrumb + Header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <span className="cursor-pointer hover:text-brand-500" onClick={() => navigate('/dashboard/school/list')}>Dashboard</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-brand-500" onClick={() => navigate('/dashboard/school/list')}>School</span>
            <span>/</span>
            <span className="text-brand-500 font-semibold truncate max-w-48">{schoolName}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">School Details</h1>
        </div>

        <div className="flex items-center gap-2">
          <span className={cn('px-3 py-1.5 rounded-lg text-xs font-bold border',
            isActive ? 'bg-green-50 text-green-600 border-green-200' : 'bg-red-50 text-red-500 border-red-200')}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
          <button onClick={() => navigate(`/dashboard/school/${schoolId}/edit`)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-brand-300 text-brand-600 text-xs font-semibold hover:bg-brand-50 transition-colors">
            <Edit2 size={12} /> Edit School Info
          </button>
          {tab === 'trainer' && (
            <button onClick={() => setAssignTrainerOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold transition-colors">
              <Plus size={12} /> Add New Trainer
            </button>
          )}
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-1 flex items-center gap-1 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={cn(
              'px-4 py-2.5 text-xs font-semibold rounded-xl whitespace-nowrap transition-colors',
              tab === t.value
                ? 'bg-brand-500 text-white shadow-sm'
                : 'text-gray-500 hover:text-brand-600 hover:bg-brand-50'
            )}
          >{t.label}</button>
        ))}
      </div>{
        // test loop of the concept - solid of module .tsx - concept of vendor user_id -- undefiend 
      }

      {/* ── Tab content ── */}
      {tab === 'details'         && <DetailsTab        school={school} />}
      {tab === 'package'         && <PackageTab        schoolId={schoolId} />}
      {tab === 'trainer'         && <TrainerTab        schoolId={schoolId} school={school} />}
      {tab === 'student'         && <StudentTab        schoolId={schoolId} />}
      {tab === 'account-summary' && <AccountSummaryTab school={school} />}

      <AssignTrainerModal
        open={assignTrainerOpen}
        onClose={() => setAssignTrainerOpen(false)}
        school={school}
        onSuccess={() => { qc.invalidateQueries({ queryKey: ['school-trainers'] }); setAssignTrainerOpen(false) }}
      />
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Assign Trainer modal — assigns an EXISTING trainer to this school.
// Mirrors the old dashboard: POST admin/trainers/create-trainer with
// { trainer_id, vendor_id, vendor_commission_in_percentage,
//   certificate_commission_in_percentage, cash_in_hand?, max_cash_in_hand_allowed?,
//   cash_clearance_date? }. Commission is validated against the school's min/max.
// ══════════════════════════════════════════════════════════════════════════════
function AssignTrainerModal({ open, onClose, school, onSuccess }: {
  open: boolean; onClose: () => void; school: any; onSuccess: () => void
}) {
  const [search, setSearch] = useState('')
  const [trainerId, setTrainerId] = useState('')
  const [vendorComm, setVendorComm] = useState('')
  const [certComm, setCertComm] = useState('')
  const [cashInHand, setCashInHand] = useState('')
  const [maxCash, setMaxCash] = useState('')
  const [clearanceDate, setClearanceDate] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const minComm = Number(school?.min_commision ?? 0)
  const maxComm = Number(school?.max_commision ?? 100)
  const minCert = Number(school?.certificate_min_commision ?? 0)
  const maxCert = Number(school?.certificate_max_commision ?? 100)

  // Active TRAINER users not necessarily yet attached to a school.
  const { data, isLoading } = useQuery({
    queryKey: ['assignable-trainers', search],
    queryFn: () => restClient
      .get(`${endpoints.users.list}?page=1&limit=1000&user_types=TRAINER&is_active=1${search ? `&search=${encodeURIComponent(search)}` : ''}`)
      .then(r => r.data),
    enabled: open,
    staleTime: 30_000,
  })
  const trainers: any[] = (data as any)?.data ?? []

  const reset = () => {
    setSearch(''); setTrainerId(''); setVendorComm(''); setCertComm('')
    setCashInHand(''); setMaxCash(''); setClearanceDate(''); setErrors({}); setSubmitError('')
  }
  const close = () => { reset(); onClose() }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!trainerId) e.trainerId = 'Please select a trainer'
    const vc = Number(vendorComm)
    if (vendorComm === '' || Number.isNaN(vc)) e.vendorComm = 'School commission is required'
    else if (vc < minComm || vc > maxComm) e.vendorComm = `Must be between ${minComm} and ${maxComm}`
    const cc = Number(certComm)
    if (certComm === '' || Number.isNaN(cc)) e.certComm = 'Certificate commission is required'
    else if (cc < minCert || cc > maxCert) e.certComm = `Must be between ${minCert} and ${maxCert}`
    if (clearanceDate && new Date(clearanceDate) > new Date()) e.clearanceDate = 'Date cannot be in the future'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const submit = async () => {
    setSubmitError('')
    if (!validate()) return
    setSaving(true)
    try {
      await schoolService.addTrainer({
        trainer_id: Number(trainerId),
        vendor_id: school.id,
        vendor_commission_in_percentage: Number(vendorComm),
        certificate_commission_in_percentage: Number(certComm),
        ...(cashInHand !== '' ? { cash_in_hand: Number(cashInHand) } : {}),
        ...(maxCash !== '' ? { max_cash_in_hand_allowed: Number(maxCash) } : {}),
        ...(clearanceDate ? { cash_clearance_date: clearanceDate } : {}),
      })
      toast.success('Trainer added successfully')
      reset()
      onSuccess()
    } catch (err) {
      setSubmitError(getApiError(err))
    } finally { setSaving(false) }
  }

  if (!open) return null
  const inCls = (err?: string) => `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${err ? 'border-red-400 focus:ring-red-300' : 'border-gray-200 focus:ring-brand-400'}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Add Trainer to {getSchoolName(school)}</h2>
          <button onClick={close} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {submitError && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-600 whitespace-pre-line">{submitError}</div>}

          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Search Trainer</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name / email…" className={inCls() + ' pl-9'} />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Select Trainer *</label>
            <select value={trainerId} onChange={e => setTrainerId(e.target.value)} className={inCls(errors.trainerId)}>
              <option value="">{isLoading ? 'Loading…' : 'Select trainer'}</option>
              {trainers.map((tr: any) => (
                <option key={tr.id} value={tr.id}>{tr.name}{tr.email ? ` [${tr.email}]` : ''}</option>
              ))}
            </select>
            {errors.trainerId && <p className="text-xs text-red-500 mt-1">{errors.trainerId}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">School Commission (%) *</label>
              <input type="number" value={vendorComm} onChange={e => setVendorComm(e.target.value)} className={inCls(errors.vendorComm)} placeholder={`${minComm}–${maxComm}`} />
              {errors.vendorComm && <p className="text-xs text-red-500 mt-1">{errors.vendorComm}</p>}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Certificate Commission (%) *</label>
              <input type="number" value={certComm} onChange={e => setCertComm(e.target.value)} className={inCls(errors.certComm)} placeholder={`${minCert}–${maxCert}`} />
              {errors.certComm && <p className="text-xs text-red-500 mt-1">{errors.certComm}</p>}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Cash in Hand</label>
              <input type="number" value={cashInHand} onChange={e => setCashInHand(e.target.value)} className={inCls()} placeholder="0" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Max Cash in Hand</label>
              <input type="number" value={maxCash} onChange={e => setMaxCash(e.target.value)} className={inCls()} placeholder="0" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Cash Clearance Date</label>
              <input type="date" value={clearanceDate} onChange={e => setClearanceDate(e.target.value)} className={inCls(errors.clearanceDate)} />
              {errors.clearanceDate && <p className="text-xs text-red-500 mt-1">{errors.clearanceDate}</p>}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white flex items-center justify-end gap-3 px-5 py-3.5 border-t border-gray-100">
          <button onClick={close} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-bold transition-colors disabled:opacity-50">
            {saving && <Spinner className="w-4 h-4" />} Add Trainer
          </button>
        </div>
      </div>
    </div>
  )
}
